import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ApiError } from '../../api/client'
import { useAction, useList } from '../../api/queries'
import type { Equipment } from '../../api/types'
import { Card, ErrorState, Field, LoadingState, PageHeader, Segmented } from '../../components/ui'
import { todayIso } from '../../lib/format'

function useEquipmentByCode() {
  const { code = '' } = useParams()
  const eq = useList<Equipment>('/api/equipment/', { code })
  return { code, eq, equipment: eq.data?.results[0] }
}

const clean = (o: Record<string, unknown>) => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== '' && v !== null && v !== undefined))

function Shell({ title, code, children }: { title: string; code: string; children: React.ReactNode }) {
  return <div className="page page--narrow"><PageHeader title={title} subtitle={<Link to={`/e/${code}`} className="mono">{code}</Link>} />{children}</div>
}

export function MeasureForm() {
  const { code, eq, equipment } = useEquipmentByCode()
  const navigate = useNavigate()
  const save = useAction<Record<string, unknown>>('post', '/api/measurements/', ['/api/measurements/', '/api/dashboard/'])
  const [state, setState] = useState<'running' | 'off'>('running')
  const [v, setV] = useState<Record<string, string>>({ readings_count: '3' })
  const [date, setDate] = useState(todayIso())
  const [error, setError] = useState<ApiError | null>(null)
  if (eq.isPending) return <LoadingState />
  if (!equipment) return <ErrorState error={eq.error ?? new ApiError(404, 'not_found', `Ningún equipo tiene el código ${code}.`)} />
  const set = (k: string) => (e: { target: { value: string } }) => setV({ ...v, [k]: e.target.value })
  const few = Number(v.readings_count) < 3
  async function submit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    const base = { equipment: equipment!.id, measured_on: date, operating_state: state, notes: v.notes }
    const body = state === 'off' ? clean(base) : clean({ ...base, nominal_power_hp: v.nominal_power_hp ?? equipment!.motor_power_hp, nominal_current_a: v.nominal_current_a ?? equipment!.motor_current_a, measured_current_a: v.measured_current_a, voltage_v: v.voltage_v ?? (equipment!.motor_voltage_v ? String(equipment!.motor_voltage_v) : ''), power_factor: v.power_factor, output_rpm: v.output_rpm, readings_count: v.readings_count ? Number(v.readings_count) : '' })
    try { await save.mutateAsync(body); navigate(`/e/${code}`) } catch (err) { setError(err as ApiError) }
  }
  const fe = error?.fieldErrors ?? {}
  return (
    <Shell title="Registrar medición" code={code}>
      <Card>
        <form className="form-stack" onSubmit={submit}>
          <Field label="Fecha de medición" htmlFor="m-date" error={fe.measured_on}><input id="m-date" type="date" max={todayIso()} value={date} onChange={(e) => setDate(e.target.value)} /></Field>
          <Segmented label="Estado de operación" value={state} onChange={setState} options={[{ value: 'running', label: 'En marcha' }, { value: 'off', label: 'Apagado' }]} />
          {state === 'off' ? <p className="callout">Un equipo apagado se guarda como “Apagado” sin valores medidos. Nada se registra como 0.</p> : (
            <>
              <Field label="Corriente medida (A)" htmlFor="m-cur" error={fe.measured_current_a} hint="Dejar vacío si no se pudo medir: se guarda como Sin medir."><input id="m-cur" inputMode="decimal" value={v.measured_current_a ?? ''} onChange={set('measured_current_a')} /></Field>
              <Field label="Voltaje (V)" htmlFor="m-v" error={fe.voltage_v}><input id="m-v" inputMode="decimal" defaultValue={equipment.motor_voltage_v ?? ''} onChange={set('voltage_v')} /></Field>
              <Field label="Factor de potencia" htmlFor="m-pf" error={fe.power_factor}><input id="m-pf" inputMode="decimal" value={v.power_factor ?? ''} onChange={set('power_factor')} /></Field>
              <Field label="Lecturas tomadas" htmlFor="m-n" error={fe.readings_count}><input id="m-n" type="number" min="1" value={v.readings_count ?? ''} onChange={set('readings_count')} /></Field>
              {few ? <p role="note" className="callout callout--warn">Tomar al menos tres lecturas: con menos, la fila se marca como “Requiere nueva medición”.</p> : null}
            </>
          )}
          <Field label="Notas" htmlFor="m-notes"><textarea id="m-notes" rows={3} value={v.notes ?? ''} onChange={set('notes')} /></Field>
          {error && !Object.keys(fe).length ? <p role="alert" className="form-error">{error.message}</p> : null}
          <button className="btn btn--primary btn--lg" disabled={save.isPending}>Guardar medición</button>
        </form>
      </Card>
    </Shell>
  )
}

export function LubricateForm() {
  const { code, eq, equipment } = useEquipmentByCode()
  const navigate = useNavigate()
  const save = useAction<Record<string, unknown>>('post', '/api/lubrication/events/', ['/api/lubrication/', '/api/dashboard/'])
  const [v, setV] = useState<Record<string, string>>({ kind: 'level_check', performed_on: todayIso() })
  const [error, setError] = useState<ApiError | null>(null)
  if (eq.isPending) return <LoadingState />
  if (!equipment) return <ErrorState error={eq.error ?? new ApiError(404, 'not_found', `Ningún equipo tiene el código ${code}.`)} />
  const set = (k: string) => (e: { target: { value: string } }) => setV({ ...v, [k]: e.target.value })
  async function submit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    try { await save.mutateAsync(clean({ equipment: equipment!.id, kind: v.kind, performed_on: v.performed_on, lubricant_type: v.lubricant_type, iso_vg: v.iso_vg ? Number(v.iso_vg) : '', quantity_liters: v.quantity_liters, operating_hours: v.operating_hours, notes: v.notes })); navigate(`/e/${code}`) } catch (err) { setError(err as ApiError) }
  }
  const fe = error?.fieldErrors ?? {}
  return (
    <Shell title="Registrar lubricación" code={code}>
      <Card>
        <form className="form-stack" onSubmit={submit}>
          <Field label="Tipo" htmlFor="l-kind" error={fe.kind}><select id="l-kind" value={v.kind} onChange={set('kind')}><option value="level_check">Revisión del nivel de aceite</option><option value="top_up">Reposición</option><option value="change">Cambio de aceite</option></select></Field>
          <Field label="Fecha" htmlFor="l-date" error={fe.performed_on}><input id="l-date" type="date" max={todayIso()} value={v.performed_on} onChange={set('performed_on')} /></Field>
          <Field label="Tipo de aceite" htmlFor="l-type" error={fe.lubricant_type}><select id="l-type" value={v.lubricant_type ?? ''} onChange={set('lubricant_type')}><option value="">Desconocido</option><option value="mineral">Mineral</option><option value="synthetic">Sintético</option></select></Field>
          <Field label="ISO VG" htmlFor="l-iso" error={fe.iso_vg}><input id="l-iso" type="number" value={v.iso_vg ?? ''} onChange={set('iso_vg')} /></Field>
          <Field label="Cantidad (litros)" htmlFor="l-q" error={fe.quantity_liters}><input id="l-q" inputMode="decimal" value={v.quantity_liters ?? ''} onChange={set('quantity_liters')} /></Field>
          <Field label="Horas de operación" htmlFor="l-h" error={fe.operating_hours} hint="Dejar vacío si se desconocen: el próximo cambio se mostrará como Desconocido, nunca se supone."><input id="l-h" inputMode="decimal" value={v.operating_hours ?? ''} onChange={set('operating_hours')} /></Field>
          <Field label="Notas" htmlFor="l-n"><textarea id="l-n" rows={3} value={v.notes ?? ''} onChange={set('notes')} /></Field>
          {error && !Object.keys(fe).length ? <p role="alert" className="form-error">{error.message}</p> : null}
          <button className="btn btn--primary btn--lg" disabled={save.isPending}>Guardar evento</button>
        </form>
      </Card>
    </Shell>
  )
}
